import React, { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
} from '@mui/material'
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle,
  Warning,
  Download,
} from '@mui/icons-material'
import { useOrgan } from '../../../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../../../layouts/PermanentSidebarMUI'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { entities } from '../../../../constants/entities'
import useBaseService from '../../../../hooks/services/useBaseService'
import useLoader from '../../../../hooks/useLoader'
import useNotification from '../../../../hooks/useNotification'
import api from '../../../../services/api'
import { downloadBlob } from '../../../../utils/downloadBlob'

export const ManageProtocolsModal = ({ data, open, onClose }) => {
  const queryClient = useQueryClient()
  const { get: baseServiceGet, post: baseServicePost } = useBaseService()
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const [uploadingForMovement, setUploadingForMovement] = useState(null)

  const downloadProtocol = useMutation({
    mutationFn: ({ documentId }) =>
      api.get(entities.shipments.documents.download(data?.id, documentId), {
        responseType: 'blob',
      }),
    onSuccess: (response, { row }) => {
      downloadBlob(response, row.original_file_name || `protocolo-${row.movement_id}.pdf`)
    },
    onError: (error) => {
      console.error('Erro ao baixar protocolo:', error)
      showNotification(
        'error',
        error.response?.data?.message || 'Não foi possível baixar o protocolo.'
      )
    },
  })

  // 1. Busca a matriz de protocolos ao abrir o modal
  const {
    data: matrixData,
    isLoading: isLoadingMatrix,
    refetch,
  } = useQuery({
    queryKey: ['protocol-matrix', data?.id],
    queryFn: async () => {
      const res = await baseServiceGet(
        entities.shipments.documents.protocolMatrix(data?.id)
      )
      return res.result
    },
    enabled: !!data?.id && open,
  })

  // 2. Mutação de Upload para um movimento específico
  const uploadProtocolo = useMutation({
    mutationFn: async ({ movementId, file }) => {
      showLoader()
      const formData = new FormData()
      formData.append('shipment_id', data?.id)
      formData.append('movement_id', movementId)
      formData.append('document_type', 'PROTOCOLO_ENTREGA_ASSINADO')
      formData.append('file', file)

      const response = await baseServicePost(
        entities.shipments.documents.uploadProtocolo(data?.id),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['packing-list'])
      refetch() // Recarrega a matriz para atualizar o status na tabela
      setUploadingForMovement(null)
      onClose()
    },
    onError: (error) => {
      showNotification(
        'error',
        error.response?.data?.message || 'Erro ao anexar.'
      )
      setUploadingForMovement(null)
    },
    onSettled: () => hideLoader(),
  })

  const handleFileSelect = (movementId, event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.size > 10 * 1024 * 1024) {
      showNotification('error', 'Arquivo deve ter no máximo 10MB.')
      event.target.value = null // limpa o input para permitir nova tentativa
      return
    }

    setUploadingForMovement(movementId)
    uploadProtocolo.mutate({ movementId, file })

    // limpar o input após iniciar o upload para permitir re-seleção em caso de falha
    setTimeout(() => {
      event.target.value = null
    }, 100)
  }

  const { selectedOrgan } = useOrgan()
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  useEffect(() => {
    if (!open) setUploadingForMovement(null)
  }, [open])

  const movements = matrixData?.movements || matrixData?.protocol_groups || []
  const totalMovements =
    matrixData?.total_movements ?? matrixData?.total_protocol_groups ?? 0
  const allAttached =
    matrixData?.protocols_attached === totalMovements

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: primaryColor,
          color: '#FFFFFF',
          p: 2,
        }}>
        <DialogTitle sx={{ fontWeight: 'bold', p: 0 }}>
          Gerenciar Protocolos de Entrega (Carregamento #{data?.id})
        </DialogTitle>
        <Chip
          label={`${matrixData?.protocols_attached || 0}/${totalMovements} Anexados`}
          color={allAttached ? 'success' : 'warning'}
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {isLoadingMatrix ? (
          <Typography align="center" sx={{ py: 4 }}>
            Carregando movimentos...
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell>
                    <strong>Nº Movimento</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Cliente</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Destino</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Ação</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((row) => {
                  // Cria um ID único para cada input de arquivo na tabela
                  const inputId = `file-upload-${row.movement_id}`

                  return (
                    <TableRow key={row.movement_id} hover>
                      <TableCell>{row.movement_number}</TableCell>
                      <TableCell>{row.customer}</TableCell>
                      <TableCell>{row.destination}</TableCell>
                      <TableCell align="center">
                        {row.has_protocol ? (
                          <Chip
                            icon={<CheckCircle />}
                            label="Anexado"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<Warning />}
                            label="Pendente"
                            color="warning"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.has_protocol ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => {
                              if (!row.document_id) {
                                showNotification(
                                  'error',
                                  'Documento do protocolo não identificado.'
                                )
                                return
                              }
                              downloadProtocol.mutate({
                                documentId: row.document_id,
                                row,
                              })
                            }}
                            disabled={downloadProtocol.isPending}>
                            Baixar
                          </Button>
                        ) : (
                          <>
                            {/* Input fica fora do botão, mas vinculado pelo ID */}
                            <input
                              id={inputId}
                              type="file"
                              hidden
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) =>
                                handleFileSelect(row.movement_id, e)
                              }
                              disabled={
                                uploadingForMovement === row.movement_id
                              }
                            />

                            {/* O Button atua como o label que aciona o input */}
                            {/* O Button atua como o label que aciona o input */}
                            <Button
                              component="label"
                              htmlFor={inputId}
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={
                                uploadingForMovement === row.movement_id ? (
                                  <Box
                                    sx={{
                                      animation: 'spin 1s linear infinite',
                                      borderRadius: '50%',
                                      width: 16,
                                      height: 16,
                                      border: '2px solid white',
                                      borderTopColor: 'transparent',
                                    }}
                                  />
                                ) : (
                                  <CloudUploadIcon />
                                )
                              }
                              disabled={
                                uploadingForMovement === row.movement_id
                              }
                              sx={{
                                color: 'white !important',
                                backgroundColor: 'primary.main',

                                '& .MuiButton-startIcon': {
                                  color: 'white !important',
                                },

                                '&.Mui-disabled': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.12)', // Fundo cinza padrão do MUI para disabled
                                },
                                '&:hover': {
                                  backgroundColor: 'primary.dark', // Escurece um pouco no hover para feedback visual
                                },
                              }}>
                              {uploadingForMovement === row.movement_id
                                ? 'Enviando...'
                                : 'Anexar'}
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoadingMatrix && movements.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum movimento vinculado a este carregamento.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={uploadingForMovement !== null}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
